import { Injectable, Inject } from '@angular/core';
import { Http } from '@angular/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs/';
import { DomainService } from './domain.service'
import { StorageService } from './storage.service'
import { ConfigService } from './config.service';
import { DOCUMENT } from '@angular/platform-browser';
import { AuthAttemptStatus } from './silent-auth.service';
import { SessionService } from './session.service';
import { EnvironmentService } from './environment.service';

@Injectable()
export class AuthService {
    private isAuthenticated: boolean;
    private isAuthenticatedObserver: Subject<boolean> = new BehaviorSubject<boolean>(this.isLoggedIn());
    public isAuthenticated$ = this.isAuthenticatedObserver.asObservable();

    private ONE_HOUR_SECONDS = 36e2;
    private ERR_REQUIRES_REFRESH = "requires_refresh";
    private is401Error = err => err.status === 401;

    constructor(
        private domainService: DomainService,
        private storageService: StorageService,
        private configService: ConfigService,
        private http: Http,
        private sessionService: SessionService,
        private environment: EnvironmentService,
        @Inject(DOCUMENT) private document: Document
    ) {

        this.isAuthenticated = this.isLoggedIn();
        this.isAuthenticated$.subscribe(
            (data: any) => {
                this.isAuthenticated = data;
            }
        );
    }

    canForceLogin() {
        let forceLogin = window.location.href.indexOf('leapest-force-login=true') > -1
        return !!forceLogin;
    }

    forceLogin() {
        const url = new URL(window.location.href);
        url.searchParams.delete('leapest-force-login');
        let params = url.hash;
        this.redirectToLoginWithExplicitParams(params);
    }

    redirectToLoginWithExplicitParams(redirectRoute: string) {
        const nonce = this.generateNonce();
        const redirect = `${window.location.origin}/${redirectRoute}`;
        const params = `${nonce}&${redirect}`;
        const url = new URL(`${this.environment.config.server.account}/login/${window.location.host.split('.')[0]}`);
        url.searchParams.append('nonce', nonce);
        url.searchParams.append('redirect', redirect);
        console.log('origin: ', window.location.origin);
        console.log('redirectRoute: ', redirectRoute);
        console.log('redirect: ', redirect);
        console.log('params: ', params);
        this.document.location.replace(url.href);
    }

    basicAuth(credentials: any): Observable<any> {
        return Observable.defer(() => {
            credentials.domain = this.domainService.getDomain().name;
            return Observable.of();
        });
    }

    consumeCredentials(authorizationToken: string, nonce: string): Observable<any> {
        let endpoint = `${this.configService.get('server').bouncer}/token/consume`;
        return this.http.post(endpoint, { authorizationToken, nonce });
    }

    setSession(accessToken: string, refreshToken: string) {
        let userDetails = this.getJwtClaims(accessToken)
        this.storageService.set('access_token', accessToken);
        this.storageService.set('refresh_token', refreshToken);
        this.sessionService.setUserDetails(userDetails);
        this.isAuthenticatedObserver.next(true);
    }

    clearSession() {
        this.storageService.delete('access_token');
        this.storageService.delete('refresh_token');
        this.sessionService.clearUserDetails();
        this.isAuthenticatedObserver.next(false);
    }

    private redirectParams(redirect?: string, nonce?: string) {
        redirect = redirect || document.location.href;
        let params = new URLSearchParams();
        params.set('redirect', redirect);
        if(nonce) params.set('nonce', nonce);
        return params.toString();
    }

    login(redirect?: string) {
        redirect = redirect ? document.location.origin + redirect : undefined;
        const params = this.redirectParams(redirect, this.generateNonce());
        this.document.location.replace(`${this.configService.get('server').account}/login/${this.domainService.getDomain().name}?${params}`);
    }

    logout() {
        this.clearSession();
        const params = this.redirectParams(`${window.location.origin}/#/welcome`);
        let domain = this.domainService.getDomain() ? this.domainService.getDomain().name : window.location.hostname;
        this.document.location.replace(`${this.configService.get('server').account}/logout/${domain}?${params}`);
    }

    getJwtClaims(token) {
        if (!token)
            return null;
        var base64 = token.split(".")[1];
        if (base64) {
            var jwtClaims = void 0;
            try {
                jwtClaims = JSON.parse(atob(base64));
                return jwtClaims;
            }
            catch (err) {
                // Malformed JWT
                return null;
            }
        }
        return null;
    }

    isLoggedIn() {
        return !!localStorage.getItem('access_token') && !!localStorage.getItem('refresh_token');
    }

    tokensExist(): boolean {
        return !!localStorage.getItem('access_token') &&
            !!localStorage.getItem('refresh_token');
    }

    public validateAccessToken(): Observable<any> {
        let accessToken = localStorage.getItem('access_token');
        let refreshToken = localStorage.getItem('refresh_token');
        let validateTokenEndpoint = `${this.configService.get('server').bouncer}/token/validate`;
        let refreshTokenEndpoint = `${this.configService.get('server').bouncer}/token/refresh`;

        return this.http.post(validateTokenEndpoint, { accessToken })
            .map(() => {
                if (this.requiresRefresh(accessToken))
                    throw new Error(this.ERR_REQUIRES_REFRESH);

                return accessToken;
            })
            .catch(err => {
                if (err.message === this.ERR_REQUIRES_REFRESH || this.is401Error(err)) {
                    return this.http.post(refreshTokenEndpoint, { refreshToken })
                        .toPromise()
                        .then((res: any) => {
                            console.log('refresh succes')
                            const { access_token } = res.json();
                            localStorage.setItem('access_token', access_token);
                            return access_token;
                        })
                        .catch(err => {
                            console.log('refresh catch')
                            this.clearSession();
                            this.logout();
                            return;
                        });
                }

                this.clearSession();
                this.logout();
                return err;
            });
    }

    public pollBouncer(): Observable<any> {
        let accessToken = localStorage.getItem('access_token');
        let validateTokenEndpoint = `${this.configService.get('server').bouncer}/token/validate`;
        if (this.requiresRefresh(accessToken)) {
            return this.http.post(validateTokenEndpoint, { accessToken })
                .catch(err => {
                    if (this.is401Error(err)) return this.refreshToken();

                    // this.clearSession();
                    return Observable.of(AuthAttemptStatus.ERROR);
                });
        }

        return Observable.of(AuthAttemptStatus.SUCCESS);
    }

    public refreshToken() {
        let refreshToken = localStorage.getItem('refresh_token');
        let refreshTokenEndpoint = `${this.configService.get('server').bouncer}/token/refresh`;

        return this.http.post(refreshTokenEndpoint, { refreshToken })
            .do((res: any) => {
                const { access_token } = res.json();
                localStorage.setItem('access_token', access_token);
                return AuthAttemptStatus.SUCCESS;
            })
            .catch((err: any) => {
                this.clearSession();
                // this.logout();
                return Observable.of(AuthAttemptStatus.UNAUTHORIZED);
            });
    }

    private requiresRefresh = (jwt: string | null): boolean => {
        const claims = this.getJwtClaims(jwt);
        const expiresAt = claims ? claims.exp : null;
        if (!expiresAt || isNaN(expiresAt)) {
          return true;
        }

        const currentEpochTime = Math.round(new Date().getTime() / 1000.0);
        const secondsLeft = expiresAt - currentEpochTime;
        return secondsLeft / this.ONE_HOUR_SECONDS < 3;
    };

    private getCurrentUser = (token: string | null): string | null => {
        const claims = this.getJwtClaims(token) || {};
        const { username = null } = claims;
        return username;
    };

    generateNonce() {
        let length: number = 16;
        const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~'
        let result = '';

        while (length > 0) {
            let bytes = new Uint8Array(16);
            let random = window.crypto.getRandomValues(bytes);
            (<any[]><any>random).forEach(function(c) {
                if (length == 0) {
                    return;
                }
                if (c < charset.length) {
                    result += charset[c];
                    length--;
                }
            });
        }

        this.storageService.set('nonce', result);
        return result;
    }

    getNonce(): string {
        return this.storageService.get('nonce');
    }

    deleteNonce(): string {
        return this.storageService.delete('nonce');
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/auth.service.ts